// Datos que comparten el riel, el panel del modulo y la barra del celular: modulos visibles para esta persona,
// en que modulo y seccion estamos, los contadores de pendientes y la ultima seccion abierta de cada modulo.
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useSession } from '../auth/session';
import { useLlamadasRecientes, useTareas } from '../data/queries';
import { porDevolver, porTipificar, seguimientosDeHoy } from '../data/selectors';
import { usePhone } from '../hooks';
import { filtrarVista } from '../lib/correo';
import { aplicarReglas, useReglas } from '../lib/reglas';
import { conHistorial, useEstadosCorreo } from '../screens/bandeja/Correos';
import { useBuzonesVisibles, useHilos } from '../screens/bandeja/useHilos';
import { useCasosPendientes } from '../screens/bandeja/useVistas';
import { type Contador, type Modulo, modulosVisibles, seccionDeRuta } from './menu';

export function useNavegacion() {
  const { puede, tieneModulo } = useSession();
  const { pathname } = useLocation();
  const modulos = useMemo(() => modulosVisibles({ puede, tieneModulo }), [puede, tieneModulo]);
  const actual = seccionDeRuta(pathname, modulos);
  if (actual) recordar(actual.modulo.id, pathname);
  return { modulos, actual, pathname };
}

export type Cuenta = { n: number; tono?: 'bad' };

export function useContadores(): Record<Contador, Cuenta> {
  const { yo } = useSession();
  const s = usePhone();
  const llamadas = useLlamadasRecientes();
  const tareas = useTareas();
  const ext = s.config.modo === 'demo' ? undefined : s.config.extension || undefined;
  // Correos no leidos: la misma consulta (cache compartida) que la pantalla Correos.
  const buzones = useBuzonesVisibles();
  const cuentas = useMemo(() => (buzones.data ?? []).map((b) => b.id), [buzones.data]);
  const correos = useHilos(cuentas, 'entrada');
  // Solo los estados (leido, archivado) de los hilos de la entrada, no la lista completa de la persona.
  const idsHilos = useMemo(() => correos.hilos.map((h) => h.id), [correos.hilos]);
  const { estados: guardados } = useEstadosCorreo(idsHilos);
  const reglas = useReglas();
  const mios = useMemo(() => (buzones.data ?? []).map((b) => b.correo.toLowerCase()), [buzones.data]);
  // Nuevos (sin abrir) + abiertos o pendientes de la persona, contados en el servidor con las reglas de las vistas.
  const casosPend = useCasosPendientes().data ?? 0;
  const noLeidos = useMemo(() => {
    const hilos = correos.hilos;
    return filtrarVista(hilos, aplicarReglas(hilos, conHistorial(hilos, guardados), reglas), mios, 'noleidos').length;
  }, [correos.hilos, guardados, reglas, mios]);
  return useMemo(() => {
    const ls = llamadas.data ?? [];
    const pend = porTipificar(ls, { extension: ext }).length;
    const dev = porDevolver(ls).length;
    const seg = seguimientosDeHoy(tareas.data ?? [], { miembroId: yo.miembroId }).length;
    const mias = (tareas.data ?? []).filter((t) => t.asignadoId === yo.miembroId).length;
    return {
      hoy: { n: pend + dev + seg, tono: dev ? 'bad' : undefined },
      llamadas: { n: pend + dev, tono: dev ? 'bad' : undefined },
      tareas: { n: mias },
      correos: { n: noLeidos },
      casos: { n: casosPend },
    };
  }, [llamadas.data, tareas.data, ext, yo.miembroId, noLeidos, casosPend]);
}

// Suma de los contadores de las secciones de un modulo (el numero que se ve en el riel).
export function cuentaDeModulo(m: Modulo, c: Record<Contador, Cuenta>): Cuenta {
  const vistos = new Set<Contador>();
  let n = 0;
  let tono: Cuenta['tono'];
  for (const s of m.secciones) {
    if (!s.contador || vistos.has(s.contador)) continue;
    vistos.add(s.contador);
    n += c[s.contador].n;
    if (c[s.contador].tono) tono = c[s.contador].tono;
  }
  return { n, tono };
}

// Al volver a un modulo se abre la ultima seccion que se uso en el (solo en esta pestana).
const CLAVE = 'consola.ultimaSeccion';
function recordar(modulo: string, ruta: string) {
  try {
    const m = JSON.parse(sessionStorage.getItem(CLAVE) ?? '{}') as Record<string, string>;
    if (m[modulo] === ruta) return;
    m[modulo] = ruta;
    sessionStorage.setItem(CLAVE, JSON.stringify(m));
  } catch {
    /* sin almacenamiento: se abre la primera seccion */
  }
}
export function destinoDeModulo(m: Modulo) {
  try {
    const r = (JSON.parse(sessionStorage.getItem(CLAVE) ?? '{}') as Record<string, string>)[m.id];
    if (r && seccionDeRuta(r, [m])) return r;
  } catch {
    /* igual que arriba */
  }
  return m.secciones[0].ruta;
}
